namespace $ {

	export type $shm_pocketbase_record = {
		id: string
		collectionName?: string
		[ key: string ]: unknown
	}

	export type $shm_pocketbase_file_item = {
		id: string
		title: string
		uri: string
		created?: string
		updated?: string
	}

	export class $shm_pocketbase_client extends $mol_object2 {

		static _realtime = {
			source: null as EventSource | null,
			client_id: '',
			topics: new Set< string >(),
		}
		static _realtime_global = new Set< string >()
		static _realtime_filtered = new Set< string >()
		static _realtime_live = new Set< string >()
		static _realtime_cache_pending = new WeakSet< object >()

		static realtime_cache_rotate( field: string, prefix: string, key: string ) {
			const cache = ( this as any )[ `${ field }()` ] as Map< string, $mol_wire_atom< any, any, any > > | undefined
			if( !cache ) return
			for( const atom of cache.values() ) {
				const candidate = String( atom.args[0] )
				if( candidate === key || candidate === prefix || !candidate.startsWith( prefix ) ) continue
				if( this._realtime_cache_pending.has( atom ) ) continue
				this._realtime_cache_pending.add( atom )
				new this.$.$mol_after_tick( ()=> {
					this._realtime_cache_pending.delete( atom )
					if( atom.sub_empty && cache.get( $mol_key( atom.args[0] ) ) === atom ) atom.destructor()
				} )
			}
		}

		static storage_prefix() {
			return 'pocketbase'
		}

		static url_store_key() {
			return `${ this.storage_prefix() }_url`
		}

		static auth_store_key( key: string ) {
			return `${ this.storage_prefix() }_auth:${ key }`
		}

		static admin_auth_store_key( key: string ) {
			return `${ this.storage_prefix() }_admin_auth:${ key }`
		}

		static auth_state( key: string ) {
			return $mol_state_local.value< string >( this.auth_store_key( key ) ) || ''
		}

		static admin_auth_state( key: string ) {
			return $mol_state_local.value< string >( this.admin_auth_store_key( key ) ) || ''
		}

		static admin_auth_token() {
			return this.admin_auth_state( 'token' )
		}

		static auth_user_token() {
			return this.auth_state( 'token' )
		}

		static auth_header_token( token: string ) {
			if( !token ) return ''
			return token.startsWith( 'Bearer ' ) ? token : `Bearer ${ token }`
		}

		static authenticated() {
			const token = this.auth_user_token()
			return Boolean( token && this.auth_valid( token ) !== false )
		}

		static admin_authenticated() {
			const token = this.admin_auth_token()
			return Boolean( token && this.admin_auth_valid( token ) )
		}

		static auth_token() {
			return this.admin_auth_token() || this.auth_user_token()
		}

		static user_id() {
			return this.auth_state( 'user_id' )
		}

		static user_record() {
			const user = this.user_id()
			if( !user ) return null
			return this.records_realtime( `users\nid = "${ this.filter_text( user ) }"` )[0] ?? null
		}

		static user_setting< Value >( key: string, next: Value | undefined, fallback: Value ) {
			const user = this.user_record()
			if( next !== undefined ) {
				if( user?.id ) this.update_record( 'users', user.id, { [ key ]: next } )
				return next
			}
			return ( user?.[ key ] as Value | undefined ) ?? fallback
		}

		static filter_text( value: string ) {
			return String( value ).replaceAll( '\\', '\\\\' ).replaceAll( '"', '\\"' )
		}

		static default_url() {
			return $mol_dom_context.location.origin
		}

		static url() {
			const override = $mol_state_local.value< string >( this.url_store_key() ) || ''
			return ( override || this.default_url() ).replace( /\/+$/, '' )
		}

		static headers(): Record< string, string > {
			const token = this.auth_token()
			return token ? { Authorization: this.auth_header_token( token ) } : {}
		}

		static user_headers(): Record< string, string > {
			const token = this.auth_user_token()
			return token ? { Authorization: this.auth_header_token( token ) } : {}
		}

		static admin_headers(): Record< string, string > {
			const token = this.admin_auth_token()
			return token ? { Authorization: this.auth_header_token( token ) } : {}
		}

		static json_headers(): Record< string, string > {
			return {
				... this.headers(),
				'Content-Type': 'application/json',
			}
		}

		static auth_reset() {
			if( this.admin_auth_token() ) {
				$mol_state_local.value( this.admin_auth_store_key( 'token' ), '' )
				$mol_state_local.value( this.admin_auth_store_key( 'user_id' ), '' )
			} else if( this.auth_state( 'token' ) ) {
				$mol_state_local.value( this.auth_store_key( 'token' ), '' )
				$mol_state_local.value( this.auth_store_key( 'user_id' ), '' )
			}

			this.realtime_reset()
		}

		static user_auth_reset() {
			$mol_state_local.value( this.auth_store_key( 'token' ), '' )
			$mol_state_local.value( this.auth_store_key( 'user_id' ), '' )
			$mol_state_local.value( this.auth_store_key( 'email' ), '' )

			this.realtime_reset()
		}

		static response_auth_failed( response: $mol_fetch_response ) {
			return response.code() === 401 || response.code() === 403
		}

		@ $mol_mem_key
		static auth_valid( token: string, next?: boolean | null ) {
			if( next !== undefined ) return next
			if( !token ) return false

			const result = $mol_error_fence(
				()=> {
					const response = $mol_fetch.response( `${ this.url() }/api/collections/users/auth-refresh`, {
						method: 'POST',
						headers: this.user_headers(),
					} )
					const data = response.json() as {
						token?: string
						record?: {
							id?: string
							email?: string
							username?: string
						}
					}

					if( !response.ok() ) {
						if( this.response_auth_failed( response ) ) this.user_auth_reset()
						return this.response_auth_failed( response ) ? false : null
					}

					if( data.token && data.token !== token ) {
						this.auth_valid( data.token, true )
						$mol_state_local.value( this.auth_store_key( 'token' ), data.token )
					}
					if( data.record?.id ) {
						$mol_state_local.value( this.auth_store_key( 'user_id' ), data.record.id )
					}
					if( data.record?.email ) {
						$mol_state_local.value( this.auth_store_key( 'email' ), data.record.email )
					}

					return true
				},
				()=> null,
				promise => { throw promise },
			)

			return result
		}

		@ $mol_mem_key
		static admin_auth_valid( token: string, next?: boolean ) {
			if( next !== undefined ) return next
			if( !token ) return false

			const result = $mol_error_fence(
				()=> {
					const response = $mol_fetch.response( `${ this.url() }/api/collections/_superusers/auth-refresh`, {
						method: 'POST',
						headers: this.admin_headers(),
					} )
					const data = response.json() as {
						token?: string
						record?: {
							id?: string
							email?: string
						}
					}

					if( !response.ok() ) {
						if( this.response_auth_failed( response ) ) this.auth_reset()
						return false
					}

					if( data.token && data.token !== token ) {
						this.admin_auth_valid( data.token, true )
						$mol_state_local.value( this.admin_auth_store_key( 'token' ), data.token )
					}
					if( data.record?.id ) {
						$mol_state_local.value( this.admin_auth_store_key( 'user_id' ), data.record.id )
					}
					if( data.record?.email ) {
						$mol_state_local.value( this.admin_auth_store_key( 'email' ), data.record.email )
					}

					return true
				},
				()=> false,
				promise => { throw promise },
			)

			return Boolean( result )
		}

		@ $mol_mem
		static file_token() {
			const token = this.auth_token()
			if( !token ) return ''

			const result = $mol_error_fence(
				()=> {
					const response = $mol_fetch.response( `${ this.url() }/api/files/token`, {
						method: 'POST',
						headers: this.headers(),
					} )
					const data = response.json() as { token?: string }
					if( !response.ok() ) return ''
					return data.token || ''
				},
				()=> '',
				promise => promise,
			)

			if( !result || $mol_promise_like( result ) ) return ''
			return result
		}

		@ $mol_mem_key
		static records( collection: string ) {
			return this.records_filtered_versioned( `${ collection }\u0000` )
		}

		@ $mol_mem_key
		static records_realtime( collection_filter: string ) {
			const [ collection ] = collection_filter.split( '\n' )
			const topic = `${ collection }/*`
			this.realtime_watch( topic )
			const key = `${ collection_filter }\u0000${ this.realtime_version( topic ) }`
			const result = this.records_filtered_versioned( key )
			this.realtime_cache_rotate( 'records_filtered_versioned', `${ collection_filter }\u0000`, key )
			return result
		}

		@ $mol_mem_key
		static records_filtered( collection_filter: string ) {
			return this.records_filtered_versioned( `${ collection_filter }\u0000` )
		}

		@ $mol_mem_key
		static records_filtered_versioned( collection_filter_version: string ) {
			const [ collection_filter ] = collection_filter_version.split( '\u0000' )
			const [ collection, filter, sort = '' ] = collection_filter.split( '\n' )
			const token = this.auth_token()
			if( !token ) return [] as readonly $shm_pocketbase_record[]

			const query = new URLSearchParams({
				perPage: '200',
			})
			if( filter ) query.set( 'filter', filter )
			if( sort ) query.set( 'sort', sort )
			const result = $mol_error_fence(
				()=> {
					const response = $mol_fetch.response(
						`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records?${ query }`,
						{ headers: this.headers() },
					)
					const data = response.json() as { items?: $shm_pocketbase_record[] }
					if( !response.ok() ) {
						if( this.response_auth_failed( response ) ) this.auth_reset()
						return [] as readonly $shm_pocketbase_record[]
					}
					return data.items ?? []
				},
				()=> [] as readonly $shm_pocketbase_record[],
				promise => promise,
			)

			if( !result || $mol_promise_like( result ) ) return [] as readonly $shm_pocketbase_record[]
			return result
		}

		@ $mol_mem_key
		static records_page( collection_filter_page: string ) {
			return this.records_page_versioned( `${ collection_filter_page }\u0000` )
		}

		@ $mol_mem_key
		static admin_records_page( collection_filter_page: string ) {
			return this.admin_records_page_versioned( `${ collection_filter_page }\u0000` )
		}

		@ $mol_mem_key
		static admin_records_page_versioned( collection_filter_page_version: string ) {
			const [ collection_filter_page ] = collection_filter_page_version.split( '\u0000' )
			const [ collection, filter, sort = '', page_text = '1', per_page_text = '50', fields = '' ] = collection_filter_page.split( '\n' )
			const token = this.admin_auth_token()
			if( !token ) return [] as readonly $shm_pocketbase_record[]

			const query = new URLSearchParams({
				page: page_text || '1',
				perPage: per_page_text || '50',
			})
			if( filter ) query.set( 'filter', filter )
			if( sort ) query.set( 'sort', sort )
			if( fields ) query.set( 'fields', fields )

			const response = $mol_fetch.response(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records?${ query }`,
				{ headers: this.admin_headers() },
			)
			const data = response.json() as { items?: $shm_pocketbase_record[] }

			if( !response.ok() ) {
				if( this.response_auth_failed( response ) ) this.auth_reset()
				throw new Error( this.response_error_message( response, data ) )
			}

			return data.items ?? []
		}

		@ $mol_mem_key
		static admin_records_page_realtime( collection_filter_page: string ) {
			const [ collection ] = collection_filter_page.split( '\n' )
			const topic = `${ collection }/*`
			this.realtime_watch( topic )
			const key = `${ collection_filter_page }\u0000${ this.realtime_version( topic ) }`
			const result = this.admin_records_page_versioned( key )
			this.realtime_cache_rotate( 'admin_records_page_versioned', `${ collection_filter_page }\u0000`, key )
			return result
		}

		@ $mol_mem_key
		static admin_records_all_versioned( collection_filter_version: string ) {
			const [ collection_filter, version = '' ] = collection_filter_version.split( '\u0000' )
			const [ collection, filter = '', sort = '', fields = '' ] = collection_filter.split( '\n' )
			const result: $shm_pocketbase_record[] = []
			for( let page = 1; page <= 1000; ++page ) {
				const page_query = `${ collection }\n${ filter }\n${ sort }\n${ page }\n500\n${ fields }`
				const page_key = `${ page_query }\u0000${ version }`
				const chunk = this.admin_records_page_versioned( page_key )
				if( version ) this.realtime_cache_rotate( 'admin_records_page_versioned', `${ page_query }\u0000`, page_key )
				result.push( ...chunk )
				if( chunk.length < 500 ) break
			}
			return result
		}

		static admin_records_all_realtime( collection_filter: string ) {
			const [ collection ] = collection_filter.split( '\n' )
			const topic = `${ collection }/*`
			this.realtime_watch( topic )
			const key = `${ collection_filter }\u0000${ this.realtime_version( topic ) }`
			const result = this.admin_records_all_versioned( key )
			this.realtime_cache_rotate( 'admin_records_all_versioned', `${ collection_filter }\u0000`, key )
			return result
		}

		@ $mol_mem_key
		static records_page_versioned( collection_filter_page_version: string ) {
			const [ collection_filter_page ] = collection_filter_page_version.split( '\u0000' )
			const [ collection, filter, sort = '', page_text = '1', per_page_text = '50', fields = '' ] = collection_filter_page.split( '\n' )
			const token = this.auth_token()
			if( !token ) return [] as readonly $shm_pocketbase_record[]

			const query = new URLSearchParams({
				page: page_text || '1',
				perPage: per_page_text || '50',
			})
			if( filter ) query.set( 'filter', filter )
			if( sort ) query.set( 'sort', sort )
			if( fields ) query.set( 'fields', fields )

			const result = $mol_error_fence(
				()=> {
					const response = $mol_fetch.response(
						`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records?${ query }`,
						{ headers: this.headers() },
					)
					const data = response.json() as { items?: $shm_pocketbase_record[] }
					if( !response.ok() ) {
						if( this.response_auth_failed( response ) ) this.auth_reset()
						return [] as readonly $shm_pocketbase_record[]
					}
					this.records_page_ready( collection_filter_page, true )
					return data.items ?? []
				},
				()=> [] as readonly $shm_pocketbase_record[],
				promise => promise,
			)

			if( !result || $mol_promise_like( result ) ) return [] as readonly $shm_pocketbase_record[]
			return result
		}

		@ $mol_mem_key
		static records_page_ready( collection_filter_page: string, next?: boolean ) {
			return next ?? false
		}

		@ $mol_mem_key
		static records_page_realtime( collection_filter_page: string ) {
			const [ collection ] = collection_filter_page.split( '\n' )
			const topic = `${ collection }/*`
			this.realtime_watch( topic )
			const key = `${ collection_filter_page }\u0000${ this.realtime_version( topic ) }`
			const result = this.records_page_versioned( key )
			this.realtime_cache_rotate( 'records_page_versioned', `${ collection_filter_page }\u0000`, key )
			return result
		}

		static records_page_realtime_filtered(
			collection_filter_page: string,
			trigger: string,
			filter: ( record: $shm_pocketbase_record )=> boolean,
		) {
			const [ collection ] = collection_filter_page.split( '\n' )
			const topic = `${ collection }/*`
			this.realtime_watch_filtered( topic, trigger, filter )
			const prefix = `${ collection_filter_page }\u0000${ trigger }\u0000`
			const key = `${ prefix }${ this.realtime_version( trigger ) }`
			const result = this.records_page_versioned( key )
			this.realtime_cache_rotate( 'records_page_versioned', prefix, key )
			return result
		}

		@ $mol_mem_key
		static realtime_live_records(
			trigger: string,
			next?: ReadonlyMap< string, $shm_pocketbase_record | null >,
		) {
			return next ?? new Map< string, $shm_pocketbase_record | null >()
		}

		static records_page_realtime_live(
			collection_filter_page: string,
			trigger: string,
			scope: ( record: $shm_pocketbase_record )=> boolean,
			matches = scope,
		) {
			const [ collection, , , , , fields = '' ] = collection_filter_page.split( '\n' )
			const selected = new Set( fields.split( ',' ).filter( Boolean ) )
			this.realtime_watch_live( `${ collection }/*`, trigger, scope, record => {
				if( !selected.size ) return record
				return Object.fromEntries(
					Object.entries( record ).filter( ([ field ])=> selected.has( field ) ),
				) as $shm_pocketbase_record
			} )

			const prefix = `${ collection_filter_page }\u0000${ trigger }\u0000`
			const key = `${ prefix }${ this.realtime_version( trigger ) }`
			const base = this.records_page_versioned( key )
			this.realtime_cache_rotate( 'records_page_versioned', prefix, key )
			const records = new Map(
				base.map( record => [ record.id, record ] ),
			)
			for( const [ id, record ] of this.realtime_live_records( trigger ) ) {
				if( record && matches( record ) ) records.set( id, record )
				else records.delete( id )
			}
			return [ ... records.values() ]
		}

		static realtime_live_remove( trigger: string, id: string ) {
			const records = new Map( this.realtime_live_records( trigger ) )
			records.set( id, null )
			this.realtime_live_records( trigger, records )
		}

		static realtime_live_reload( trigger: string ) {
			this.realtime_live_records( trigger, new Map() )
			this.realtime_version( trigger, Date.now() )
		}

		static response_error_message( response: any, data: unknown ) {
			const body = data as { message?: unknown; data?: Record< string, { message?: unknown; code?: unknown } > } | null
			const details = body?.data
				? Object.entries( body.data ).map( ([ field, error ])=> {
					const message = error?.message || error?.code || 'invalid value'
					return `${ field }: ${ message }`
				} )
				: []

			return [
				body?.message || response.message?.() || 'PocketBase request failed',
				... details,
			].map( String ).filter( Boolean ).join( '\n' )
		}

		static create_record( collection: string, data: Record< string, unknown > ) {
			const response = $mol_fetch.response(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records`,
				{
					method: 'POST',
					headers: this.json_headers(),
					body: JSON.stringify( data ),
				},
			)
			const body = response.json() as $shm_pocketbase_record
			if( !response.ok() ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static update_record( collection: string, id: string, data: Record< string, unknown > ) {
			const response = $mol_fetch.response(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records/${ encodeURIComponent( id ) }?cors=patch2`,
				{
					method: 'PATCH',
					headers: this.json_headers(),
					body: JSON.stringify( data ),
				},
			)
			const body = response.json() as $shm_pocketbase_record
			if( !response.ok() ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static async update_record_async( collection: string, id: string, data: Record< string, unknown > ) {
			const response = await $mol_dom_context.fetch(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records/${ encodeURIComponent( id ) }?cors=patch2`,
				{
					method: 'PATCH',
					headers: this.json_headers(),
					body: JSON.stringify( data ),
				},
			)
			const text = await response.text()
			const body = text ? JSON.parse( text ) as $shm_pocketbase_record : {} as $shm_pocketbase_record
			if( !response.ok ) throw new Error( String( body.message || response.statusText || 'PocketBase request failed' ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static delete_record( collection: string, id: string ) {
			const response = $mol_fetch.response(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records/${ encodeURIComponent( id ) }`,
				{
					method: 'DELETE',
					headers: this.headers(),
				},
			)
			const body = response.ok() ? null : response.json() as $shm_pocketbase_record
			if( !response.ok() ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static create_form_record( collection: string, form: FormData ) {
			const response = $mol_fetch.response(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records`,
				{
					method: 'POST',
					headers: this.headers(),
					body: form,
				},
			)
			const body = response.json() as $shm_pocketbase_record
			if( !response.ok() ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static async create_form_record_async( collection: string, form: FormData ) {
			const response = await $mol_dom_context.fetch(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records`,
				{
					method: 'POST',
					headers: this.headers(),
					body: form,
				},
			)
			const text = await response.text()
			const body = text ? JSON.parse( text ) as $shm_pocketbase_record : {} as $shm_pocketbase_record
			if( !response.ok ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		static async update_form_record_async( collection: string, id: string, form: FormData ) {
			const response = await $mol_dom_context.fetch(
				`${ this.url() }/api/collections/${ encodeURIComponent( collection ) }/records/${ encodeURIComponent( id ) }?cors=patch2`,
				{
					method: 'PATCH',
					headers: this.headers(),
					body: form,
				},
			)
			const text = await response.text()
			const body = text ? JSON.parse( text ) as $shm_pocketbase_record : {} as $shm_pocketbase_record
			if( !response.ok ) throw new Error( this.response_error_message( response, body ) )
			this.realtime_version( `${ collection }/*`, Date.now() )
			return body
		}

		@ $mol_mem_key
		static realtime_version( topic: string, next?: number ) {
			return next ?? 0
		}

		static realtime_source( topic: string ) {
			const added = !this._realtime.topics.has( topic )
			this._realtime.topics.add( topic )

			const exists = this._realtime.source
			if( exists ) {
				if( added && this._realtime.client_id ) this.realtime_subscribe()
				return exists
			}
			if( !$mol_dom_context.EventSource ) return

			const source = new $mol_dom_context.EventSource( `${ this.url() }/api/realtime` )
			this._realtime.source = source

			source.addEventListener( 'PB_CONNECT', event => {
				const data = JSON.parse( event.data )
				this._realtime.client_id = data.clientId || ''
				if( this._realtime.client_id ) this.realtime_subscribe()
			} )

			source.addEventListener( 'error', ()=> {
				this._realtime.client_id = ''
			} )

			return source
		}

		static realtime_subscribe() {
			if( !this._realtime.client_id ) return
			$mol_dom_context.fetch( `${ this.url() }/api/realtime`, {
				method: 'POST',
				headers: this.json_headers(),
				body: JSON.stringify({
					clientId: this._realtime.client_id,
					subscriptions: [ ... this._realtime.topics ],
				}),
			} ).catch( ()=> null )
		}

		static realtime_reset() {
			this._realtime.source?.close()
			this._realtime.source = null
			this._realtime.client_id = ''
			this._realtime.topics.clear()
			this._realtime_global.clear()
			this._realtime_filtered.clear()
			this._realtime_live.clear()
		}

		static realtime_watch( topic: string ) {
			if( this._realtime_global.has( topic ) ) return
			const source = this.realtime_source( topic )
			if( !source ) return

			this._realtime_global.add( topic )
			source.addEventListener( topic, ()=> {
				this.realtime_version( topic, Date.now() )
			} )
		}

		static realtime_watch_filtered(
			topic: string,
			trigger: string,
			filter: ( record: $shm_pocketbase_record )=> boolean,
		) {
			const key = `${ topic }\u0000${ trigger }`
			if( this._realtime_filtered.has( key ) ) return
			const source = this.realtime_source( topic )
			if( !source ) return

			this._realtime_filtered.add( key )
			source.addEventListener( topic, event => {
				try {
					const data = JSON.parse( event.data )
					const record = data.record || data
					if( filter( record ) ) this.realtime_version( trigger, Date.now() )
				} catch( error ) {
					console.warn( '[shm_pocketbase_client] realtime filter failed', error )
				}
			} )
		}

		static realtime_watch_live(
			topic: string,
			trigger: string,
			scope: ( record: $shm_pocketbase_record )=> boolean,
			project: ( record: $shm_pocketbase_record )=> $shm_pocketbase_record,
		) {
			const key = `${ topic }\u0000${ trigger }`
			if( this._realtime_live.has( key ) ) return
			const source = this.realtime_source( topic )
			if( !source ) return

			this._realtime_live.add( key )
			source.addEventListener( topic, event => {
				try {
					const data = JSON.parse( event.data )
					const record = ( data.record || data ) as $shm_pocketbase_record
					if( !record.id || !scope( record ) ) return

					const records = new Map( this.realtime_live_records( trigger ) )
					const next = data.action === 'delete' ? null : project( record )
					if( $mol_compare_deep( records.get( record.id ), next ) ) return
					records.set( record.id, next )
					this.realtime_live_records( trigger, records )
				} catch( error ) {
					console.warn( '[shm_pocketbase_client] realtime live update failed', error )
				}
			} )
		}

		static record_files( record: $shm_pocketbase_record, field: string ) {
			const value = record[ field ]
			if( Array.isArray( value ) ) return value.map( String ).filter( Boolean )
			return value ? [ String( value ) ] : []
		}

		static file_uri( record: $shm_pocketbase_record, collection: string, file: string ) {
			const collection_name = encodeURIComponent( record.collectionName || collection )
			return `${ this.url() }/api/files/${ collection_name }/${ encodeURIComponent( record.id ) }/${ encodeURIComponent( file ) }`
		}

		@ $mol_mem_key
		static files( collection: string, field: string ) {
			const items = [] as $shm_pocketbase_file_item[]

			for( const record of this.records( collection ) ) {
				for( const file of this.record_files( record, field ) ) {
					const uri = this.file_uri( record, collection, file )
					items.push({
						id: uri,
						title: String( record.title || record.id ),
						uri,
						created: String( record.created ?? '' ),
						updated: String( record.updated ?? '' ),
					})
				}
			}

			return items
		}

	}

}
